import { rem } from 'polished'
import React, { useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SearchForm from 'components/Search/SearchForm'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import config from 'constants/config'
import { convertDatesToString, encodeSearchParams, isSearchEnabled } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import { useTrendingDestinationsLabels } from './hooks/userTrendingLabels'
import zIndex from 'styles/tools/z-index'
import useOptimizelyExperiment from '../../../../hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import HotelsTabContent from 'components/SearchV2/Components/Mobile/TabContents/HotelsTabContent'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import { useLocation } from 'react-router'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

const HeroContainer = styled(HeroImage)`
  min-height: unset;
  padding: ${rem(40)} 0;

  &.expanded-mobile-search {
    padding-bottom: ${rem(140)};
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
  }
`

const SolidHeroContainer = styled.div`
  min-height: unset;
  padding: ${rem(40)} 0;
  background-color: ${props => props.theme.palette.neutral.default.seven};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
  }
`

const HotelSearchContainerPaneContent = styled.div<{ $useBrandBorder?: boolean; $useDropShadow?: boolean }>`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: ${props => (props.$useBrandBorder ? `1px solid ${props.theme.palette.brand.primary.normal}` : 'none')};
  box-shadow: ${props => (props.$useDropShadow ? `0 ${rem(8)} ${rem(24)} rgba(0, 0, 0, 0.12)` : 'none')};

  &.mobile-expanded {
    margin-top: ${rem(16)};
    margin-bottom: ${rem(16)};
  }
`

const HotelSearchBarFloating = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  width: 100%;
  z-index: ${zIndex.floatingPanel};

  &.floating-search-bar {
    position: relative;
    transform: unset;
    margin-top: ${rem(32)};
  }
`

interface Props {
  floatingSearchBar?: boolean
  useInlineSearchBar?: boolean
  heroTitle?: React.ReactNode
  heroSubtitle?: React.ReactNode
  useDarkBackground?: boolean
  useBrandBorder?: boolean
  searchBasePath?: string
  searchBaseMapPath?: string
  useDropShadow?: boolean
  heroImageId?: string
}

function HotelPageSearchHero({
  floatingSearchBar,
  useInlineSearchBar = false,
  heroTitle,
  heroSubtitle,
  useDarkBackground = false,
  useBrandBorder = false,
  searchBasePath = '/search',
  searchBaseMapPath,
  useDropShadow = false,
  heroImageId,
  trendingDestinations,
  isSearchEnabled,
  filters,
}: Props & MappedStateProps) {
  const intl = useIntl()
  const trendingDestinationsLabels = useTrendingDestinationsLabels(trendingDestinations)
  const locationLabels = useLocationSearchLabels()

  const defaultHeroTitle = useMemo(
    () =>
      intl.formatMessage(
        {
          id: 'CUktKBLdZ4',
          defaultMessage: '<italic>Hotels & Resorts</italic> to dream about',
          description: 'Default hero title for hotel search page with italic styling',
        },
        {
          italic: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      ),
    [intl],
  )

  const defaultHeroSubtitle = intl.formatMessage({
    id: '/a+UsLiWMg',
    defaultMessage: 'Choose from our curated hotels & resorts',
    description: 'Default hero subtitle for hotel search page',
  })

  const searchButtonText = intl.formatMessage({
    id: 'dS7fcdq7CP',
    defaultMessage: 'Search',
    description: 'Search button text on hotel search page',
  })

  const searchHotelsPlaceholder = intl.formatMessage({
    id: '43csoeRK9I',
    defaultMessage: 'Search hotels in',
    description: 'Placeholder text for hotel search input on mobile',
  })

  const resolvedHeroTitle = heroTitle ?? defaultHeroTitle
  const resolvedHeroSubtitle = heroSubtitle ?? defaultHeroSubtitle

  const naturalLanguageSearch = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)
  const isMobileDevice = useIsMobileDevice()
  const hotelSearchExperimentVariation = useOptimizelyExperiment(
    OptimizelyExperiments.croHotelSearchDirectlyFromVerticalPageMobileWeb,
    isMobileDevice,
  )
  const isHotelSearchDirectlyOnPageVariant = getOptimizelyEnabledDecision(hotelSearchExperimentVariation ?? null)

  const globalSearchState = useContext(GlobalSearchStateContext)
  const { search } = useLocation()
  const dispatch = useAppDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const shouldInlineSearchBar = useInlineSearchBar || !!floatingSearchBar
  const resolvedSearchMapPath = searchBaseMapPath ?? `${searchBasePath}/map`

  const onSearch = () => {
    if (formRef.current?.reportValidity()) {
      const {
        searchItem,
        occupancies,
        checkinDate,
        checkoutDate,
        flexibleNights,
        isFlexibleDateSelected,
        flexibleMonths,
        userSelectedFlexibleMonths,
      } = globalSearchState

      const dates = convertDatesToString(ISO_DATE_FORMAT, checkinDate, checkoutDate)
      const queryParams = encodeSearchParams({
        urlSearch: search,
        searchItem,
        filters,
        dates,
        rooms: occupancies,
        isFlexibleWithDate: isFlexibleDateSelected,
        flexibleNights,
        flexibleMonths,
        userSelectedFlexibleMonths,
      }).toString()

      dispatch(pushWithRegion(`${searchBasePath}?${queryParams}`))
    }
  }

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearch()
  }

  if (!isSearchEnabled) {
    return null
  }

  const renderSearchHeroContent = () => (
    <>
      <LayoutContainer relative>
        <Group direction="vertical" horizontalAlign="start" tabletHorizontalAlign="center" gap={8}>
          <Heading colour={useDarkBackground ? 'neutral-one' : 'neutral-eight'} variant="heading2" className="hero-heading center">
            {resolvedHeroTitle}
          </Heading>
          <BodyText variant="large" colour={useDarkBackground ? 'neutral-one' : 'neutral-eight'} className="center">
            {resolvedHeroSubtitle}
          </BodyText>
        </Group>
      </LayoutContainer>

      <HotelSearchBarFloating className={clsx({ 'floating-search-bar': shouldInlineSearchBar })}>
        <LayoutContainer>
          <CSSBreakpoint min="tablet">
            <HotelSearchContainerPaneContent $useBrandBorder={useBrandBorder} $useDropShadow={useDropShadow}>
              <SearchForm
                submitOnApply
                typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
                baseSearchPath={searchBasePath}
                baseSearchMapPath={resolvedSearchMapPath}
                locationSearchPlaceholder={naturalLanguageSearch ? locationLabels.placeholderLabelNaturalLanguage : locationLabels.placeholderLabel}
                label={naturalLanguageSearch ? locationLabels.titleLabelNaturalLanguage : locationLabels.titleLabel}
                showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
              />
            </HotelSearchContainerPaneContent>
          </CSSBreakpoint>

          <CSSBreakpoint max="mobile">
            {isHotelSearchDirectlyOnPageVariant && <HotelSearchContainerPaneContent className="mobile-expanded">
              <VerticalSpacer as="form" onSubmit={onSearchSubmit} gap={12} ref={formRef}>
                <HotelsTabContent isInPage onSearch={onSearch} />
                <TextButton fit="mobile-full-width" kind="primary" type="submit" size="large">{searchButtonText}</TextButton>
              </VerticalSpacer>
            </HotelSearchContainerPaneContent>}
            {!isHotelSearchDirectlyOnPageVariant && <GlobalMobileSearchHeroBar
              placeholder={searchHotelsPlaceholder}
              placeholderTextList={trendingDestinationsLabels}
              showAIGlowBorder={naturalLanguageSearch}
            />}
          </CSSBreakpoint>
        </LayoutContainer>
      </HotelSearchBarFloating>
    </>
  )

  if (useDarkBackground) {
    return (
      <SolidHeroContainer className={clsx({ 'expanded-mobile-search': isHotelSearchDirectlyOnPageVariant })}>
        {renderSearchHeroContent()}
      </SolidHeroContainer>
    )
  }

  return (
    <HeroContainer
      className={clsx({ 'expanded-mobile-search': isHotelSearchDirectlyOnPageVariant })}
      gravity="auto"
      aria-hidden="true"
      brightness={-25}
      id={heroImageId ?? config.searchFormSection.hotelsImageId}
      tabletAspectRatio="16:3"
    >
      {renderSearchHeroContent()}
    </HeroContainer>
  )
}

interface MappedStateProps {
  trendingDestinations: Array<App.TrendingDestination>
  isSearchEnabled: boolean
  filters: App.OfferListFilters
}

const mapStateToProps = (state: App.State): MappedStateProps => ({
  trendingDestinations: state.destination.trendingDestinations,
  isSearchEnabled: isSearchEnabled(),
  filters: getUrlOfferListFilters(state),
})

export default connect(mapStateToProps)(HotelPageSearchHero)
